import React, {useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {
  IntegratedSorting,
  SortingState,
  SelectionState,
  FilteringState,
  IntegratedFiltering,
} from '@devexpress/dx-react-grid';
import {
  Grid,
  VirtualTable,
  TableHeaderRow,
  TableSelection,
  TableFilterRow,
} from '@devexpress/dx-react-grid-material-ui';
import {useWindowResize} from '@ozgene/gp5.hooks.use-window-resize';
import {ColumnDefinedFilterCell} from 'util/components/ColumnDefinedFilterCell';
import {CustomRowComponent} from 'util/components/CustomRowComponent';
import {ColumnDefinedCell} from 'util/components/ColumnDefinedCell';
import {CustomNoDataCell} from 'util/components/CustomNoDataCell';
import DateProvider from 'util/components/DateProvider';

export default function ProjectList(props) {
  const {
    projects,
    onSelectionChange,
    projectFilterText,
    dateFilterText,
    onChangeProjectFilterText,
    onChangeDateFilterText,
    selectedId,
  } = props;
  const [filters, setFilters] = useState([
    projectFilterText.length > 0 && {
      columnName: 'name',
      operation: 'contains',
      value: projectFilterText,
    },
    dateFilterText.length > 0 && {
      columnName: 'createdDt',
      operation: 'contains',
      value: dateFilterText,
    },
  ]);
  const {height} = useWindowResize();
  const projectTableColumns = [
    {
      name: 'name',
      title: 'Name',
    },
    {
      name: 'createdDt',
      title: 'Created Date',
      getCellValue: (row) => row.createdDt,
    },
  ];
  const columnExtensions = [{columnName: 'createdDt', width: 200}];
  const handleFiltersChange = useCallback(
    (newFilters) => {
      const projectFilter = newFilters.find(({columnName}) => columnName === 'name');
      const dateFilter = newFilters.find(({columnName}) => columnName === 'createdDt');
      const projectFilterValue = projectFilter?.value ?? '';
      const dateFilterValue = dateFilter?.value ?? '';

      if (projectFilterValue !== projectFilterText) {
        onChangeProjectFilterText(projectFilterValue);
      }

      if (dateFilterValue !== dateFilterText) {
        onChangeDateFilterText(dateFilterValue);
      }

      setFilters(newFilters);
    },
    [dateFilterText, onChangeDateFilterText, onChangeProjectFilterText, projectFilterText],
  );

  return (
    <Grid rows={projects} columns={projectTableColumns} getRowId={({id}) => id}>
      <FilteringState filters={filters} onFiltersChange={handleFiltersChange} />
      <SortingState defaultSorting={[{columnName: 'createdDt', direction: 'desc'}]} />
      <SelectionState selection={[selectedId]} onSelectionChange={onSelectionChange} />
      <IntegratedFiltering />
      <IntegratedSorting />
      <DateProvider for={['createdDt']} />
      <VirtualTable
        columnExtensions={columnExtensions}
        height={height - 140}
        cellComponent={ColumnDefinedCell}
        noDataCellComponent={CustomNoDataCell}
      />
      <TableHeaderRow showSortingControls />
      <TableSelection
        selectByRowClick
        highlightRow
        showSelectionColumn={false}
        rowComponent={CustomRowComponent}
      />
      <TableFilterRow showFilterSelector cellComponent={ColumnDefinedFilterCell} />
    </Grid>
  );
}

ProjectList.propTypes = {
  projects: PropTypes.array.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  projectFilterText: PropTypes.string.isRequired,
  dateFilterText: PropTypes.string.isRequired,
  onChangeProjectFilterText: PropTypes.func.isRequired,
  onChangeDateFilterText: PropTypes.func.isRequired,
  selectedId: PropTypes.string,
};
